
import { computed, defineComponent, onBeforeUnmount, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// composable
import useTimer from '@/common/composable/useTimer'
import useNotifications from '@/common/composable/useNotifications'

// realisations
import { verification } from '@/core/realisations/verification.realisation'
import { dispatcher } from '@/core/realisations/dispatcher.realisation'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-button': TheButton,
    },
    props: {
        deliveryId: {
            type: Number,
            required: true,
        },
        companyId: {
            type: Number,
            required: true,
        },
        invoiceNumber: {
            type: String,
            required: true,
        },
        phoneNumber: {
            type: String,
            required: true,
        },
    },
    emits: ['cancel', 'submited'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const timer = useTimer()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            sms_code: null as string | null,
        })

        const state_form$ = useVuelidate({ sms_code: { required } }, state)

        const emitCancel = () => {
            emit('cancel')
        }

        const sms_state = reactive({
            is_failed: false,
            is_loading: false,
            hash: null as string | null,
        })

        const isHashReceived = computed(() => Boolean(sms_state.hash))

        const verification_state = reactive({
            is_loading: false,
            is_failed: false,
            is_verified: false,
        })

        const sendSMS = async () => {
            try {
                sms_state.is_loading = true

                if (!isHashReceived.value) {
                    // Send SMS
                    const form_data = new FormData()
                    form_data.append('companyId', props.companyId.toString())
                    form_data.append('invoiceNumber', props.invoiceNumber)
                    form_data.append('phoneNumber', props.phoneNumber)

                    const { data } = await verification.sendSMS(form_data)
                    sms_state.hash = data.hash
                } else {
                    // Repeat SMS
                    if (!sms_state.hash) {
                        notifications.notify({
                            title: 'Ошибка верификации',
                            description: 'HASH не получен',
                            type: 'danger',
                        })

                        return
                    }

                    const form_data = new FormData()
                    form_data.append('hash', sms_state.hash)

                    const { data } = await verification.sendSMSRepeat(form_data)
                    sms_state.hash = data.hash
                }

                timer.start(60)

                sms_state.is_failed = false
            } catch (error) {
                sms_state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При отправке СМС кода произошла ошибка',
                })
            } finally {
                sms_state.is_loading = false
            }
        }

        const verify = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (!sms_state.hash) {
                notifications.notify({
                    title: 'Ошибка верификации',
                    description: 'HASH не получен',
                    type: 'danger',
                })

                return
            }

            if (!state.sms_code) return

            try {
                verification_state.is_loading = true

                const form_data = new FormData()
                form_data.append('hash', sms_state.hash)
                form_data.append('code', state.sms_code)

                await verification.confirm(form_data)

                verification_state.is_verified = true
                verification_state.is_failed = false

                notifications.notify({
                    title: 'Заказ верифицирован',
                    description: 'Теперь можете закрыть доставку',
                    type: 'success',
                })
            } catch (error) {
                verification_state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При верификации произошла ошибка',
                })
            } finally {
                verification_state.is_loading = false
            }
        }

        const approveDelivery = async () => {
            try {
                state.is_loading = true

                const form_data = new FormData()
                form_data.append('delivery_receiver_name', props.phoneNumber)

                await dispatcher.approveDelivery(props.deliveryId, form_data)
                notifications.notify({ title: 'Доставка закрыта', type: 'success' })

                state.is_failed = false
                emit('submited')
            } catch (error) {
                state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При закрытии доставки прозиошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeUnmount(() => {
            timer.clear()
        })

        const onConfirm = () => {
            if (verification_state.is_verified) {
                approveDelivery()
                return
            }

            verify()
        }

        const modalConfirmText = computed(() => {
            if (verification_state.is_verified) return 'Закрыть доставку'
            return 'Верифицировать'
        })

        return {
            state,
            state_form$,
            emitCancel,
            onConfirm,
            sms_state,
            verification_state,
            timer_state: timer.state,
            isTimerActive: timer.isTimerActive,
            sendSMS,
            isHashReceived,
            modalConfirmText,
        }
    },
})
